import React, { useContext } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import fileDownload from 'js-file-download';
import Typography from '@mui/material/Typography';
import { datetimeToVisualString } from '../../Utils/DateTimeParser';
import { appVersion } from '../../Constants';
import type { Theme} from '@mui/material';
import { useTheme } from '@mui/material';
import type BugReport from '../../Models/BugReportModel';
import type { BugReportContextProps } from '../../Contexts/BugReportContext';
import BugReportContext from '../../Contexts/BugReportContext';

type BugReportDialogProps = {
    controller: boolean,
    onClose: () => void,
};

function BugReportDialog(props: Readonly<BugReportDialogProps>): JSX.Element {

    const bugReportContext: BugReportContextProps = useContext<BugReportContextProps>(BugReportContext); //try first to only store bugreports in context (not persistent) as this application has very limited persistent data and as it is a bit tricky to store this in a good way in localstorage (and don't want to introduce indexedDB just for that)

    const currentTheme: Theme = useTheme();

    function handleDownloadBugReportFile(): void {

        const fileContent: string = 'K-Fleet Vessel Manager Bug Report\t' + appVersion + '\n\n' + [...bugReportContext.bugReports].sort((a: BugReport, b: BugReport) => a.timestamp.getTime() - b.timestamp.getTime()).map((bugReport: BugReport) => {
            return datetimeToVisualString(bugReport.timestamp, false, 0) + ' UTC\t' + bugReport.header + '\t' + bugReport.message;
        }).join('\n\n');

        fileDownload(fileContent, 'KFleetVesselManagerBugReport.txt');
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={props.onClose}
        >
            <DialogTitle>Send Bug Report</DialogTitle>
            <DialogContent>
                <Typography>
                    We are sorry to hear you are having difficulties with our application.
                </Typography>
                <br />
                <Typography>
                    To send a bug report, first download the bug report by clicking the button below.
                </Typography>
                <br />
                <Typography style={{
                    wordWrap: 'break-word',
                }}>
                    Then send an email to <a
                        href='mailTo:bugreport.fleet@km.kongsberg.com'
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                            bugreport.fleet@km.kongsberg.com
                    </a>
                </Typography>
                <br />
                <Typography>
                    Remember to attach the downloaded file to the email before sending.
                </Typography>
                <br />
                <Typography>
                    <span style={{ fontWeight: 'bold' }}>Privacy Notice:</span> By sending this bug report, you accept that information about your device and K-Fleet usage relevant to the encountered bug will be sent to the K-Fleet Support Team. Feel free to have a look at the downloaded file before sending it if you want to. It will only be used for resolving the bug. We will treat this report as confidential, and never share it with any third parties. If you have any questions, please contact us at km.support.fleet@km.kongsberg.com.
                </Typography>
                <br />
                <div style={{
                    textAlign: 'center',
                }}>
                    <Button 
                        onClick={handleDownloadBugReportFile}
                        color='primary'
                        variant='contained'
                        style={{
                            minWidth: '150px',
                            maxWidth: '150px',
                            minHeight: '40px',
                            maxHeight: '40px',
                            lineHeight: 'normal',
                        }}
                    >
                        Download Bug Report
                    </Button>
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={props.onClose}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BugReportDialog;